import React, { useEffect, useState } from 'react';
import { View, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import { Drawer, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import I18n from '../../../../components/shared/i18n/I18n.component';
import ImageIcon from '../../../../components/shared/Icon/ImageIcon.component';
import ButtonV2 from '../../../../components/shared/Buttons/ButtonV2.container';
import colors from '../../../../config/colors';
import httpService from '../../../../services/http.service';
import { modalService } from '../../../../services/modal.service';
import AlertModal from '../../../../modals/AlertModal/AlertModal.component';
import {
	NotificationModal,
	onDownload,
	PermissionRow,
	PermissionSelection,
} from './UpdateMultiplePermissionsDrawer.utils';

type UpdateMultiplePermissionsDrawerProps = {
	open: boolean;
	onClose: () => void;
	selectAll: boolean;
	selectedItems: number[];
	selectedFilters: any;
	refreshPage: () => void;
};

const useStyles = makeStyles({
	paper: {
		width: 450,
		maxWidth: '100%',
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
		padding: 24,
		boxSizing: 'border-box',
		overflow: 'hidden',
	},
});

/**
 * Side sheet for the bulk employee-permission update. Each permission row exposes an
 * add / remove pill pair - pressing the selected pill deselects it. The update logic
 * (confirm -> API -> result alert) is unchanged from the former modal.
 */
const UpdateMultiplePermissionsDrawer = ({
	open,
	onClose,
	selectAll,
	selectedItems,
	selectedFilters,
	refreshPage,
}: UpdateMultiplePermissionsDrawerProps) => {
	const classes = useStyles();

	const [roles, setRoles] = useState([]);
	const [checkedRoles, setCheckedRoles] = useState<
		Record<string, PermissionSelection>
	>({});
	const [sending, setSending] = useState(false);

	useEffect(() => {
		if (!open) {
			return;
		}
		// the drawer is reused - a previous session's selection must not leak in
		setCheckedRoles({});
		setSending(false);
		getRolePermissions();
	}, [open]);

	const getRolePermissions = async () => {
		const res: any = await httpService.api({
			type: 'getRolePermissions',
			params: { id: 6 },
			query: { unitId: selectedFilters?.unitIds.join(',') },
		});
		if (res) {
			setRoles(res);
		}
	};

	const onRoleChange = (roleId: number, val: PermissionSelection | null) => {
		setCheckedRoles(prev => {
			if (val === null) {
				const next = { ...prev };
				delete next[roleId];
				return next;
			}
			return { ...prev, [roleId]: val };
		});
	};

	const sortPermisions = () => {
		const added = [];
		const removed = [];
		Object.keys(checkedRoles).forEach(roleId => {
			if (checkedRoles[roleId] === 'ADD') {
				added.push(parseInt(roleId));
			} else if (checkedRoles[roleId] === 'REMOVE') {
				removed.push(parseInt(roleId));
			}
		});

		return { added, removed };
	};

	const onSendPressed = async () => {
		const { added, removed } = sortPermisions();

		await modalService.openModal(
			null,
			{
				submitBtnText: 'general.close',
				iconName: 'attention',
				hideCloseBtn: true,
			},
			(props: any) => (
				<NotificationModal
					{...props}
					onAccept={onNext => onNext(onUpdate(added, removed))}
				/>
			),
		);
	};

	const onUpdate = async (added, removed) => {
		setSending(true);
		try {
			const res = await httpService.api({
				type: 'updateMultiplePermisions',
				data: {
					f: selectedFilters,
					profileIds: selectAll ? [] : selectedItems,
					addPermissionIds: added,
					removePermissionIds: removed,
				},
				disableBI: true,
			});
			if (!!res?.jobId) {
				modalService.openModal(
					null,
					{
						submitBtnText: 'general.close',
					},
					(props: any) => (
						<AlertModal
							{...props}
							onSubmit={next => next(onClose(refreshPage()))}
						>
							<View style={{ alignItems: 'flex-end' }}>
								<I18n>updateUserermisions.requestLoading</I18n>
							</View>
						</AlertModal>
					),
				);
			} else {
				const textToShow =
					res?.totalFailed === 0 && res?.totalSucess > 0
						? 'updateUserermisions.UPDATE_ROLE_SUCCESS'
						: res?.totalSucess === 0 && res?.totalFailed > 0
						? 'updateUserermisions.UPDATE_ROLE_FAILED'
						: 'updateUserermisions.UPDATE_ROLE_PARTIAL';

				modalService.openModal(
					null,
					{
						hideCloseBtn: true,
					},
					({ onNext }: any) => (
						<View style={styles.resultModal}>
							<View style={styles.resultModalHeader}>
								<I18n color='primaryDarkBlue' font='medium' size={18}>
									updateUserermisions.title
								</I18n>

								<TouchableOpacity
									onPress={() => onNext(onClose(refreshPage()))}
								>
									<CloseIcon
										style={{ fontSize: 20, color: colors.greyText }}
									/>
								</TouchableOpacity>
							</View>

							<View style={styles.resultModalText}>
								<I18n
									color='clockRecordBlue'
									users={res?.totalElements}
									usersSucces={res?.totalSucess || ''}
									usersFailed={res.totalFailed || ''}
								>
									{textToShow}
								</I18n>
								<TouchableOpacity
									onPress={() => onDownload(res?.result)}
									style={{ marginTop: 5 }}
								>
									<I18n size={16} weight='normal' color='primary'>
										updateUserermisions.details
									</I18n>
								</TouchableOpacity>
							</View>

							<View style={styles.resultModalButtons}>
								<ButtonV2
									preset='primary'
									onPress={() => onNext(onClose(refreshPage()))}
								>
									<I18n size={16} weight='normal' color='white'>
										general.close
									</I18n>
								</ButtonV2>
							</View>
						</View>
					),
				);
			}
		} catch (e) {
			modalService.openModal(
				null,
				{
					submitBtnText: 'general.close',
				},
				(props: any) => (
					<AlertModal {...props} onSubmit={next => next(onClose())}>
						<View style={{ alignItems: 'flex-end' }}>
							<I18n>updateUserermisions.requestFailed</I18n>
						</View>
					</AlertModal>
				),
			);
		} finally {
			setSending(false);
		}
	};

	const selectedCount = Object.keys(checkedRoles).length;

	return (
		<Drawer
			anchor='left'
			open={open}
			onClose={onClose}
			classes={{ paper: classes.paper }}
		>
			<View style={styles.header}>
				<I18n size={17} color='primaryDarkBlue' font='bold'>
					updateUserermisions.title
				</I18n>
				<TouchableOpacity
					onPress={onClose}
					hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
				>
					<CloseIcon style={{ fontSize: 20, color: colors.greyText }} />
				</TouchableOpacity>
			</View>

			<I18n size={14} color='clockRecordBlue' style={styles.description}>
				updateUserermisions.description
			</I18n>

			{/* only this region scrolls - the header, description and footer stay pinned */}
			<ScrollView style={styles.body}>
				{roles.map((item: any) => (
					<PermissionRow
						key={item.id}
						data={item}
						value={checkedRoles[item.id]}
						onChange={val => onRoleChange(item.id, val)}
					/>
				))}
			</ScrollView>

			<View style={styles.footer}>
				{selectedCount > 0 && (
					<View style={styles.countLine}>
						<ImageIcon
							name='infoCircle'
							width={18}
							height={18}
							color={colors.clockRecordBlue}
							style={{ marginLeft: 6 }}
						/>
						<I18n size={14} color='clockRecordBlue' count={selectedCount}>
							updateUserermisions.selectedCount
						</I18n>
					</View>
				)}

				<View style={styles.buttons}>
					<ButtonV2 preset='outline' onPress={onClose}>
						<I18n size={16} weight='normal' color='titlePrimary'>
							general.cancel
						</I18n>
					</ButtonV2>
					<ButtonV2
						preset='primary'
						onPress={onSendPressed}
						disabled={selectedCount === 0}
					>
						<I18n size={16} weight='normal' color='white'>
							general.update
						</I18n>
					</ButtonV2>

				</View>
			</View>

			{sending && (
				<View style={styles.sendingOverlay}>
					<ActivityIndicator color={colors.primary} size='large' />
				</View>
			)}
		</Drawer>
	);
};

const styles = StyleSheet.create({
	header: {
		flexDirection: 'row-reverse',
		alignItems: 'center',
		justifyContent: 'space-between',
		flexShrink: 0,
	},
	description: {
		marginTop: 16,
		textAlign: 'right',
		flexShrink: 0,
	},
	// minHeight lets the region shrink so it scrolls instead of pushing the footer out
	body: {
		flex: 1,
		minHeight: 0,
		marginTop: 24,
	},
	footer: {
		flexShrink: 0,
		marginTop: 16,
	},
	countLine: {
		flexDirection: 'row-reverse',
		alignItems: 'center',
		justifyContent: 'flex-start',
		marginBottom: 24,
	},
	buttons: {
		flexDirection: 'row-reverse',
		alignItems: 'center',
		justifyContent: 'flex-end',
		gap: 16,
	},
	sendingOverlay: {
		position: 'absolute',
		left: 0,
		right: 0,
		top: 0,
		bottom: 0,
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: colors.backgroundOpacity,
	},
	resultModal: {
		padding: 32,
		width: 420,
		backgroundColor: colors.white,
		borderRadius: 20,
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	resultModalHeader: {
		width: '100%',
		flexDirection: 'row-reverse',
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	// flex-end keeps the text and the details link on the right, as the RTL copy expects
	resultModalText: {
		marginVertical: 20,
		alignItems: 'flex-end',
	},
	resultModalButtons: {
		width: '100%',
		flexDirection: 'row',
		justifyContent: 'flex-start',
	},
});

export default UpdateMultiplePermissionsDrawer;
